import {
  OrchestratorActionStatus,
  OrchestratorActionType,
  WebsiteMigration,
} from '@/services/WebsiteMigrations/interfaces';

export interface WebsiteComposite {
  website: Website,
  details: WebsiteDetails,
  staging: WebsiteStaging,
  alias?: WebsiteAlias;
  group?: WebsiteGroup;
}

export interface WebsiteDetails {
  id: number;
  vhostId: number | null;
  sslCertificateId: number | null;
  unixUserId: number;
  databaseIds: number[];
}

export interface WebsiteAlias {
  parentId: number;
}

export interface WebsiteStaging {
  parentId: number;
}

export interface WebsiteGroup {
  id: number;
  mainWebsite: number;
  owner: string;
}

export interface Website {
  id: number;

  domain: string;

  originServer: string;

  userId: string;

  details: WebsiteDetails;

  staging: WebsiteStaging | null;

  status: string;

  createdAt: string;
}

export interface WebsiteCreationActionLog {
  id: number;
  log: string;
  createdDate: string;
}

export interface WebsiteCreationAction {
  id: number;
  action: string;
  parentActionsIds: number[];
  logs: WebsiteCreationActionLog[];
  status: string;
  createdDate: string;
  updatedDate: string;
}

export interface WebsiteCreation {
  id: number;
  websiteId: number;
  actions: WebsiteCreationAction[];
  status: string;
  createdDate: string;
  updatedDate: string;
}

export interface WebsiteDeletionActionLog {
  id: number;
  log: string;
  createdDate: string;
}

export interface WebsiteDeletion {
  id: number;
  websiteId: number;
  status: string;
  type: string;
  actions: WebsiteDeletionAction[];
  createdDate: string;
  updatedDate: string;
}

export interface WebsiteDeletionAction {
  id: number;
  action: string;
  parentActionsIds: number[];
  deletion: WebsiteDeletion;
  logs: WebsiteDeletionActionLog[];
  status: string;
  createdDate: string;
  updatedDate: string;
}

export enum WebsiteEnvironment {
  ALIAS = 'alias',
  SUB = 'sub',
}

export enum ToggleActionStatus {
  PENDING = 'pending',
  IN_PROGRESS = 'in progress',
  FAILED = 'failed',
  RETRY = 'retry',
  RETRYING = 'retrying',
  DONE = 'done',
}

export interface ToggleLog {
  id: number;
  log: string;
}

export enum WebsiteCreationStatus {
  PENDING = 'pending',
  DONE = 'done',
  FAILED = 'failed',
}

export enum WebsiteCreationActionStatus {
  PENDING = 'pending',
  IN_PROGRESS = 'in progress',
  FAILED = 'failed',
  RETRY = 'retry',
  RETRYING = 'retrying',
  DONE = 'done',
  ROLLING_BACK = 'rolling back',
  ROLLED_BACK = 'rolled back',
  CANCELLED = 'cancelled',
}

export enum WebsiteStatus {
  PENDING = 'PENDING',
  ACTIVE = 'ACTIVE',
  DISABLED = 'DISABLED',
  ERROR = 'ERROR',
}

export interface WebsiteToggleAction {
  id: number;
  action: string;
  status: ToggleActionStatus;
  logs: ToggleLog[];
}

export enum WebsiteToggleState {
  DISABLE = 'disable',
  ENABLE = 'enable',
}

export enum WebsiteToggleStatus {
  PENDING = 'pending',
  DONE = 'done',
  FAILED = 'failed',
}

export interface WebsiteToggle {
  id: number;
  websiteId: number;
  actions: WebsiteToggleAction[];
  state: WebsiteToggleState;
  status: WebsiteToggleStatus;
}

export interface WebsiteSearchResponseInterface {
  Websites: Website[],
  count: number
}

export interface WebsiteOrchestratorResourcesInterface {
  creations?: WebsiteCreation[];
  deletions?: WebsiteDeletion[];
  migrations?: WebsiteMigration[];
}

export enum WebsiteOrchestratorResourceType {
  CREATION = 0,
  DELETION = 1,
  MIGRATION = 2,
}

export interface WebsiteOrchestratorResourceInterface {
  type: WebsiteOrchestratorResourceType;
  creation?: WebsiteCreation;
  deletion?: WebsiteDeletion;
  migration?: WebsiteMigration;
}

export default interface WebsiteRepositoryInterface {
  getWebsiteById(id: number): Promise<WebsiteComposite>;

  searchWebsites(
    page?: number,
    domainSearch?: string,
    userId?: string,
    serverSearch?: string,
    statusSearch?: string,
  ): Promise<WebsiteSearchResponseInterface>;

  getWebsitesByIds(ids: number[], page?: number): Promise<Website[]>;

  getByServerAndDomain(server: string, domain?: string, page?: number): Promise<Website[]>;

  getWebsiteCreations(id: number): Promise<WebsiteCreation[]>;

  createWebsite(
    server: string,
    domain: string,
    userId: string,
    groupId: number | null | undefined,
    excludeActions: string[],
    plan: number,
  ): Promise<Website>;

  getWebsiteDeletions(id: number): Promise<WebsiteDeletion[]>;

  createSubwebsite(websiteId: number, domain: string, userId: string, websiteEnvironment: string, excludeActions: string[]): Promise<Website>;

  createWebsiteStaging(websiteId: number, websiteEnvironment: string, userId: string): Promise<Website>;

  createWebsiteAlias(websiteId: number, domain: string, excludeActions: string[], websiteEnvironment: string, userId: string): Promise<Website>;

  deleteWebsite(websiteId: number): Promise<void>;

  toggleWebsite(websiteId: number, status: string): Promise<void>;

  getWebsiteToggles(id: number): Promise<WebsiteToggle[]>;

  recreateWebsite(id: number): Promise<void>;

  retryAction(websiteId: number, creationId: number, actionId: number): Promise<void>;

  getWebsiteOrchestratorResources(websiteId: number): Promise<WebsiteOrchestratorResourcesInterface>;

  getWebsiteOrchestratorResource(
    websiteId: number,
    resourceId: number,
    resourceType: number,
  ): Promise<WebsiteOrchestratorResourceInterface>;

  updateActionStatus(
    websiteId: number,
    actionId: number,
    actionParentId: number,
    actionType: OrchestratorActionType,
    status: OrchestratorActionStatus,
  ): Promise<void>
}
